import React from "react";
import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const SwipableImageCarousel = ({
  images,
  getImage,
  imageUrls,
  navigation = "dots",
  aspectRatio = "2/1",
}) => {
  const [current, setCurrent] = React.useState(0);
  const [dragX, setDragX] = React.useState(0);
  const touchStartX = React.useRef(null);
  const containerRef = React.useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && current < images.length - 1) {
        setCurrent(current + 1);
      } else if (diff < 0 && current > 0) {
        setCurrent(current - 1);
      }
    }

    setDragX(0);
    touchStartX.current = null;
  };

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      if (touchStartX.current === null) return;
      const diff = touchStartX.current - e.touches[0].clientX;
      const horizontalDistance = Math.abs(diff);
      const lockThreshold = el.offsetWidth * 0.05;

      if (horizontalDistance > lockThreshold && e.cancelable) {
        e.preventDefault();
      }

      let newDragX = -diff;
      const maxDrag = el.offsetWidth * 0.3;

      if (current === 0 && newDragX > 0) {
        newDragX = Math.min(newDragX, maxDrag);
      }
      if (current === images.length - 1 && newDragX < 0) {
        newDragX = Math.max(newDragX, -maxDrag);
      }

      setDragX(newDragX);
    };

    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [current, images.length]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio,
        // maxWidth: '500px',
        // borderRadius: '15px',
        overflow: "hidden",
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <Box
        sx={{
          display: "flex",
          transform: `translateX(calc(-${current * 100}% + ${dragX}px))`,
          transition: dragX !== 0 ? "none" : "transform 0.3s ease",
          height: "100%",
        }}
      >
        {imageUrls.map((url, idx) => (
          <Box key={idx} sx={{ minWidth: "100%", height: "100%" }}>
            <CardMedia
              component="img"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              image={url}
              alt=""
            />
          </Box>
        ))}
      </Box>
      {images.length > 1 && navigation === "dots" && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {images.map((_, idx) => (
            <Box
              key={idx}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: idx === current ? "#fff" : "rgba(255,255,255,0.5)",
                transition: "background-color 0.3s",
              }}
            />
          ))}
        </Stack>
      )}
      {images.length > 1 && navigation === "fraction" && (
        <Box
          sx={{
            position: "absolute",
            bottom: 35,
            right: 15,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <Typography sx={{ color: "#fff", fontSize: "12px" }}>
            {current + 1}/{images.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SwipableImageCarousel;
